import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  StopCircle, 
  Trash2, 
  AlertTriangle, 
  Zap, 
  RefreshCw, 
  CheckCircle2, 
  XCircle,
  Shield,
  Loader2,
  Play,
  Pause
} from 'lucide-react';
import { toast } from 'sonner';
import { adminOperationsService, StaleOperationStats, CorruptedModuleStats, OrphanedRecordStats } from '@/services/adminOperationsService';

interface AutoBatchState {
  isRunning: boolean;
  currentBatch: number;
  totalBatches: number;
  itemsProcessed: number;
  totalItems: number;
  startTime: number | null;
}

export const MasterControlPanel = () => {
  const [staleStats, setStaleStats] = useState<StaleOperationStats | null>(null);
  const [corruptedStats, setCorruptedStats] = useState<CorruptedModuleStats | null>(null);
  const [orphanedStats, setOrphanedStats] = useState<OrphanedRecordStats>({ content: 0, quizzes: 0, labs: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({
    stopStale: false,
    cleanOrphans: false,
    fixCorrupted: false,
    generateAll: false
  });
  const [autoBatch, setAutoBatch] = useState<AutoBatchState>({
    isRunning: false,
    currentBatch: 0,
    totalBatches: 0,
    itemsProcessed: 0,
    totalItems: 0,
    startTime: null
  });
  const autoBatchRef = useRef<boolean>(false);

  const fetchStats = async () => {
    try {
      // Fetch stale operations
      const stale = await adminOperationsService.getStaleOperationStats();
      setStaleStats(stale);

      // Fetch corrupted modules
      const corrupted = await adminOperationsService.getCorruptedModules();
      setCorruptedStats(corrupted);

      // Fetch truly orphaned records (module_id doesn't exist in course_modules AT ALL)
      const orphaned = await adminOperationsService.getOrphanedRecordStats();
      setOrphanedStats(orphaned);

    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleStopStaleOperations = async () => {
    setActionLoading(prev => ({ ...prev, stopStale: true }));
    try {
      const result = await adminOperationsService.stopAllStaleOperations();
      toast.success(`Stopped ${result.stopped} stale operations`);
      if (result.errors.length > 0) {
        toast.warning(`Some errors: ${result.errors.join(', ')}`);
      }
      fetchStats();
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, stopStale: false }));
    }
  };

  const handleCleanAllOrphans = async () => {
    setActionLoading(prev => ({ ...prev, cleanOrphans: true }));
    try {
      const result = await adminOperationsService.cleanAllOrphanedRecords();
      const total = result.archivedContent + result.deletedQuizzes + result.deletedLabs;
      toast.success(`Cleaned ${total} orphaned records (${result.archivedContent} content, ${result.deletedQuizzes} quizzes, ${result.deletedLabs} labs)`);
      if (result.errors.length > 0) {
        toast.warning(`Some errors: ${result.errors.slice(0, 3).join(', ')}`);
      }
      fetchStats();
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, cleanOrphans: false }));
    }
  };

  const handleFixCorruptedModules = async () => {
    setActionLoading(prev => ({ ...prev, fixCorrupted: true }));
    try {
      const result = await adminOperationsService.disableCorruptedModules();
      toast.success(`Disabled ${result.disabled} corrupted modules`);
      if (result.errors.length > 0) {
        toast.warning(`Some errors: ${result.errors.join(', ')}`);
      }
      fetchStats();
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, fixCorrupted: false }));
    }
  };

  const handleGenerateAll = async () => {
    setActionLoading(prev => ({ ...prev, generateAll: true }));
    try {
      const result = await adminOperationsService.triggerUniversalGeneration('all');
      if (result.success) {
        toast.success(`Generation started! Operation ID: ${result.operationId}`);
      } else {
        toast.error(`Failed: ${result.error}`);
      }
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, generateAll: false }));
    }
  };

  // Auto-batch generation - runs batches continuously until all gaps are filled
  const startAutoBatch = async () => {
    autoBatchRef.current = true;
    const startTime = Date.now();
    
    // Get initial gap counts
    const gaps = await adminOperationsService.getContentGaps();
    const totalItems = gaps.content + gaps.quizzes + gaps.labs;
    const estimatedBatches = Math.ceil(totalItems / 50);
    
    setAutoBatch({
      isRunning: true,
      currentBatch: 0,
      totalBatches: estimatedBatches,
      itemsProcessed: 0,
      totalItems,
      startTime
    });

    toast.info(`Starting auto-batch generation for ${totalItems} items (~${estimatedBatches} batches)`);

    let batchCount = 0;
    let consecutiveEmptyBatches = 0;

    while (autoBatchRef.current && consecutiveEmptyBatches < 3) {
      batchCount++;
      
      try {
        const result = await adminOperationsService.triggerUniversalGeneration('all');
        
        if (!result.success) {
          // If active operation exists, poll until it completes instead of retrying immediately
          if (result.error?.includes('active enhancement operation')) {
            console.log('Active operation detected, waiting for completion...');
            
            // Poll for active operation to complete
            let waitAttempts = 0;
            while (waitAttempts < 120 && autoBatchRef.current) { // Wait up to 6 minutes
              await new Promise(r => setTimeout(r, 3000));
              const activeOp = await adminOperationsService.getActiveOperation();
              
              if (!activeOp) {
                console.log('Previous operation completed, proceeding...');
                break;
              }
              
              // Update UI with active operation progress
              setAutoBatch(prev => ({
                ...prev,
                itemsProcessed: activeOp.processed_items || prev.itemsProcessed
              }));
              
              waitAttempts++;
            }
            continue;
          }
          
          console.error('Batch failed:', result.error);
          await new Promise(r => setTimeout(r, 10000));
          continue;
        }

        // Wait for batch to complete (poll operation status)
        let completed = false;
        let attempts = 0;
        while (!completed && attempts < 120 && autoBatchRef.current) { // Wait up to 6 minutes
          await new Promise(r => setTimeout(r, 3000));
          const status = await adminOperationsService.getOperationStatus(result.operationId!);
          
          if (status?.status === 'completed' || status?.status === 'failed' || status?.status === 'cancelled') {
            completed = true;
            const processed = status.processed_items || 0;
            
            if (processed === 0) {
              consecutiveEmptyBatches++;
            } else {
              consecutiveEmptyBatches = 0;
            }

            setAutoBatch(prev => ({
              ...prev,
              currentBatch: batchCount,
              itemsProcessed: prev.itemsProcessed + processed
            }));
          } else if (status) {
            // Update progress while waiting
            setAutoBatch(prev => ({
              ...prev,
              itemsProcessed: Math.max(prev.itemsProcessed, status.processed_items || 0)
            }));
          }
          attempts++;
        }

        // Refresh gap counts
        const newGaps = await adminOperationsService.getContentGaps();
        const remaining = newGaps.content + newGaps.quizzes + newGaps.labs;
        
        if (remaining === 0) {
          toast.success('🎉 All content gaps filled!');
          break;
        }

        setAutoBatch(prev => ({
          ...prev,
          totalItems: remaining + prev.itemsProcessed,
          totalBatches: Math.ceil(remaining / 50) + batchCount
        }));

      } catch (error: any) {
        console.error('Auto-batch error:', error);
        await new Promise(r => setTimeout(r, 5000));
      }
    }

    const elapsed = Math.round((Date.now() - startTime) / 1000 / 60);
    toast.success(`Auto-batch complete! ${batchCount} batches in ${elapsed} minutes`);
    
    setAutoBatch(prev => ({ ...prev, isRunning: false }));
    autoBatchRef.current = false;
    fetchStats();
  };

  const stopAutoBatch = () => {
    autoBatchRef.current = false;
    setAutoBatch(prev => ({ ...prev, isRunning: false }));
    toast.info('Auto-batch generation stopped');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Control Panel...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const hasIssues = (staleStats?.total || 0) > 0 || (corruptedStats?.count || 0) > 0 || orphanedStats.total > 0;

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Master Control Panel
            </CardTitle>
            <CardDescription>One-click actions for database maintenance and content generation</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={`p-3 rounded-lg border ${staleStats?.total ? 'bg-destructive/10 border-destructive/30' : 'bg-success/10 border-success/30'}`}>
            <div className="flex items-center gap-2">
              {staleStats?.total ? <XCircle className="h-4 w-4 text-destructive" /> : <CheckCircle2 className="h-4 w-4 text-success" />}
              <span className="text-sm font-medium">Stale Ops</span>
            </div>
            <span className="text-2xl font-bold">{staleStats?.total || 0}</span>
          </div>

          <div className={`p-3 rounded-lg border ${corruptedStats?.count ? 'bg-warning/10 border-warning/30' : 'bg-success/10 border-success/30'}`}>
            <div className="flex items-center gap-2">
              {corruptedStats?.count ? <AlertTriangle className="h-4 w-4 text-warning" /> : <CheckCircle2 className="h-4 w-4 text-success" />}
              <span className="text-sm font-medium">Corrupted</span>
            </div>
            <span className="text-2xl font-bold">{corruptedStats?.count || 0}</span>
          </div>

          <div className={`p-3 rounded-lg border ${orphanedStats.total ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300' : 'bg-success/10 border-success/30'}`}>
            <div className="flex items-center gap-2">
              {orphanedStats.total ? <Trash2 className="h-4 w-4 text-yellow-600" /> : <CheckCircle2 className="h-4 w-4 text-success" />}
              <span className="text-sm font-medium">Orphaned</span>
            </div>
            <span className="text-2xl font-bold">{orphanedStats.total}</span>
          </div>

          <div className="p-3 rounded-lg border bg-primary/10 border-primary/30">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Actions</span>
            </div>
            <span className="text-2xl font-bold">{hasIssues ? 'Needed' : 'None'}</span>
          </div>
        </div>

        {/* Critical Alert */}
        {hasIssues && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Issues Detected</AlertTitle>
            <AlertDescription>
              {staleStats?.total ? `${staleStats.total} stale operations, ` : ''}
              {corruptedStats?.count ? `${corruptedStats.count} corrupted modules, ` : ''}
              {orphanedStats.total ? `${orphanedStats.total} orphaned records` : ''}
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button
            variant={staleStats?.total ? "destructive" : "outline"}
            onClick={handleStopStaleOperations}
            disabled={actionLoading.stopStale || !staleStats?.total}
            className="w-full"
          >
            {actionLoading.stopStale ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <StopCircle className="h-4 w-4 mr-2" />
            )}
            Stop Stale ({staleStats?.total || 0})
          </Button>

          <Button
            variant={corruptedStats?.count ? "destructive" : "outline"}
            onClick={handleFixCorruptedModules}
            disabled={actionLoading.fixCorrupted || !corruptedStats?.count}
            className="w-full"
          >
            {actionLoading.fixCorrupted ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Fix Corrupted ({corruptedStats?.count || 0})
          </Button>

          <Button
            variant={orphanedStats.total ? "destructive" : "outline"}
            onClick={handleCleanAllOrphans}
            disabled={actionLoading.cleanOrphans || !orphanedStats.total}
            className="w-full"
          >
            {actionLoading.cleanOrphans ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Clean Orphans ({orphanedStats.total})
          </Button>

          <Button
            variant="default"
            onClick={handleGenerateAll}
            disabled={actionLoading.generateAll}
            className="w-full"
          >
            {actionLoading.generateAll ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Generate Missing
          </Button>
        </div>

        {/* Auto-Batch Generation Section */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Auto-Batch Generation
              </h4>
              <p className="text-xs text-muted-foreground">
                Continuously generates all missing content, quizzes, and labs in batches of 50
              </p>
            </div>
            {autoBatch.isRunning ? (
              <Button variant="destructive" size="sm" onClick={stopAutoBatch}>
                <Pause className="h-4 w-4 mr-2" />
                Stop
              </Button>
            ) : (
              <Button variant="default" size="sm" onClick={startAutoBatch}>
                <Play className="h-4 w-4 mr-2" />
                Start Auto-Batch
              </Button>
            )}
          </div>

          {autoBatch.isRunning && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Batch {autoBatch.currentBatch} / ~{autoBatch.totalBatches}</span>
                <span>{autoBatch.itemsProcessed} / {autoBatch.totalItems} items</span>
              </div>
              <Progress 
                value={autoBatch.totalItems > 0 ? (autoBatch.itemsProcessed / autoBatch.totalItems) * 100 : 0} 
              />
              {autoBatch.startTime && (
                <div className="text-xs text-muted-foreground text-center">
                  Running for {Math.round((Date.now() - autoBatch.startTime) / 1000 / 60)} minutes
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detailed breakdown */}
        {orphanedStats.total > 0 && (
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Orphaned breakdown: {orphanedStats.content} content, {orphanedStats.quizzes} quizzes, {orphanedStats.labs} labs
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MasterControlPanel;
